/* eslint-disable react-refresh/only-export-components */
import type { ThemeConfig } from 'antd/es/config-provider';
import theme from 'antd/es/theme';
import { createGlobalStyle } from 'styled-components';
import tinycolor from 'tinycolor2';

export const lightColorPalette = {
  primary: '#003459',
  secondary: '#497B9E',
  tertiary: '#f2f2f0',
  error: '#ff4d4f',
  success: '#52c41a',
  warning: '#faad14',
  background: '#f2f2f0',
} as const;

export const darkColorPalette = {
  primary: '#42789F',
  secondary: '#5f8bab',
  tertiary: '#222222',
  error: '#ff7875',
  success: '#73d13d',
  warning: '#ffc53d',
  background: '#222222',
} as const;

export const colorPalette = lightColorPalette;

// Add a type for the theme
export type ThemeMode = 'light' | 'dark';

// Create a function to get the color palette based on theme
export const getColorPalette = (mode: ThemeMode) => {
  return mode === 'light' ? lightColorPalette : darkColorPalette;
};

// Modify the color derivation to accept a theme parameter
export const deriveColors = (mode: ThemeMode) => {
  const palette = getColorPalette(mode);
  return {
    colorPrimary: palette.primary,
    colorPrimaryText: tinycolor(palette.primary)
      [mode === 'light' ? 'darken' : 'lighten'](80)
      .toString(),
    colorFillSecondary: tinycolor(palette.secondary)
      [mode === 'light' ? 'darken' : 'lighten'](3)
      .toString(),
    colorBgBase: palette.background,
    colorBgContainer: tinycolor(palette.tertiary)
      [mode === 'light' ? 'lighten' : 'darken'](3)
      .toString(),
    colorBgElevated: tinycolor(palette.background)
      [mode === 'light' ? 'lighten' : 'darken'](3)
      .toString(),
    colorBgLayout: tinycolor(palette.background)
      [mode === 'light' ? 'lighten' : 'darken'](3)
      .toString(),
    colorBorder: tinycolor(palette.tertiary)
      [mode === 'light' ? 'darken' : 'lighten'](3)
      .toString(),
    colorBorderBg: palette.background,
    colorError: palette.error,
    colorTextBase: tinycolor(palette.primary)
      [mode === 'light' ? 'darken' : 'lighten'](80)
      .toString(),
    colorTextSecondary: tinycolor(palette.primary)
      [mode === 'light' ? 'darken' : 'lighten'](80)
      .toString(),
    secondaryColor: palette.tertiary,
    surfaceColor: tinycolor(palette.tertiary)
      [mode === 'light' ? 'lighten' : 'darken'](2)
      .toString(),
    textColor: tinycolor(palette.primary)
      [mode === 'light' ? 'darken' : 'lighten'](45)
      .toString(),
    colorTextHeading: tinycolor(palette.primary)
      [mode === 'light' ? 'darken' : 'lighten'](80)
      .toString(),
  };
};

// Modify the theme creation to accept a theme parameter
export const createCustomTheme = (mode: ThemeMode): ThemeConfig => {
  const colors = deriveColors(mode);

  return {
    algorithm: [
      mode === 'light' ? theme.defaultAlgorithm : theme.darkAlgorithm,
      theme.compactAlgorithm,
    ],
    token: {
      colorPrimary: colors.colorPrimary,
      colorBgBase: colors.colorBgBase,
      colorBgElevated: colors.colorBgElevated,
      colorPrimaryText: colors.colorPrimaryText,
      colorTextSecondary: colors.colorTextSecondary,
      colorTextHeading: colors.colorTextHeading,
      colorFillSecondary: colors.colorFillSecondary,
      colorBorderBg: colors.colorBorderBg,
      colorBgContainer: colors.colorBgContainer,
      colorBgLayout: colors.colorBgLayout,
      colorBorder: colors.colorBorder,
      colorTextBase: colors.colorTextBase,
      colorError: colors.colorError,
      colorLink: tinycolor(colors.colorPrimaryText)
        [mode === 'light' ? 'darken' : 'lighten'](30)
        .toString(),
    },
    components: {
      Divider: {
        colorSplit: colors.colorBorder,
      },
      Typography: {
        titleMarginTop: 0,
        padding: 0,
      },
      Button: {
        dangerShadow: colors.colorError,
        colorFill:
          mode === 'light' ? colors.colorBgContainer : colors.colorBgBase,
      },
      Layout: {
        headerBg: colors.colorBgBase,
        siderBg: colors.colorBgBase,
        margin: 0,
        padding: 0,
        headerHeight: 55,
      },
      Menu: {
        itemSelectedBg: colors.secondaryColor,
        itemSelectedColor: colors.textColor,
        itemHeight: 35,
      },
      Tag: {
        defaultBg: colors.surfaceColor,
      },
      Card: {
        colorBgContainer: tinycolor(colors.surfaceColor)
          [mode === 'light' ? 'darken' : 'lighten'](2)
          .toString(),
      },
      Tree: {
        indentSize: 8,
      },
      Table: {
        borderColor: colors.colorBorder,
      },
      Progress: {
        defaultColor: colors.colorPrimary,
        remainingColor: tinycolor(colorPalette.tertiary).darken(10).toString(),
      },
      Steps: {},
      Skeleton: {
        colorFillContent: tinycolor(colorPalette.secondary)
          .lighten(10)
          .toString(),
      },
    },
  };
};
export const MODAL_WIDTH = 500;

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.05) transparent;

  &:hover {
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    transition: background-color 0.2s;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.15);
  }

  &::-webkit-scrollbar-thumb:active {
    background-color: rgba(0, 0, 0, 0.2);
  }
` as React.ExoticComponent & { customTheme: ThemeConfig };

export default GlobalStyle;
